import { getOrThrow } from 'quartz/globalState/api';
import { TRACKING_CONTEXT } from './state/globalState';

/**
 * Exposes a wrapper for `.track` that requests the method within the globalStateProvider
 *
 * @param appName The event namespace (as defined in the Usage Event Library), not the Quartz package name.
 * @param eventName The name of the event to track. It should be in Title Case format. Check go/usagelibrary for more details.
 * @param properties An object containing the properties to be sent with the event. The keys should be in camelCase format, and the values should be strings or numbers. Check go/usagelibrary for more details.
 *
 * @example
 * ```ts
 * import { track } from 'quartz-tracking/service';
 *
 * track('My App', 'Event Name', { someProperty: 'someValue' });
 * ```
 *
 * @remarks
 * This function is not meant to be used outside of your React Application,
 * and will throw if used before App initialization or module root.
 */
export const track = (appName, eventName, properties) => {
  const {
    track: trackFn
  } = getOrThrow(TRACKING_CONTEXT);
  trackFn(appName, eventName, properties);
};

/**
 * Allows consumers to subscribe to tracker switches (e.g. anonymous → base)
 *
 * This is useful to subscribe when the tracker is about to change or be initialized for the first time,
 *   which allows synchronous operations, like, overriding the tracker Device Id, or what not.
 *
 * @example
 * ```ts
 * import { onTrackerSwitch } from 'quartz-tracking/service';
 *
 * onTrackerSwitch(() => console.log('Tracker was switched!'));
 * ```
 *
 * @example
 * ```ts
 * import { onTrackerSwitch } from 'quartz-tracking/service';
 * import { setDeviceId } from 'usage-tracker-core/client/defaultTrackers';
 *
 * onTrackerSwitch(() => {
 *   const { uuid } = new URLSearchParams(window.location.search);
 *
 *   setDeviceId(uuid);
 * });
 * ```
 *
 * @remarks
 * Listeners added with this function will persist across tracker switches,
 * and should not be added in infinite loops, like inside React components without proper dependencies.
 */
export const onTrackerSwitch = callback => {
  const {
    onTrackerSwitch: subscribeFn
  } = getOrThrow(TRACKING_CONTEXT);
  subscribeFn(callback);
};